import React, { useState } from 'react';
import { PhysicalButton } from './PhysicalButton';

/**
 * RemoveGoalModal
 *
 * Sub-modal shown when operator presses - on a team score.
 * Shows existing goals for the team and lets operator pick which one to remove.
 */
export const RemoveGoalModal = ({
  isOpen,
  onClose,
  teamName,
  goals,
  onConfirm,
}) => {
  const [selectedEventId, setSelectedEventId] = useState(null);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedEventId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedEventId) return;
    onConfirm(selectedEventId);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-lg shadow-2xl border border-red-600/30 max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-red-900/50 via-gray-800 to-red-900/50 rounded-t-lg">
          <h3 className="text-lg font-bold text-red-400 tracking-wide text-center">
            REMOVE GOAL — {teamName}
          </h3>
        </div>

        {/* Goal List */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0" style={{ maxHeight: '300px' }}>
          {goals.length > 0 ? (
            <div className="space-y-2">
              {goals.map((goal) => {
                const playerName = goal.player
                  ? `${goal.player.first_name || ''} ${goal.player.last_name || ''}`.trim()
                  : 'Unknown Player';
                const typeLabel = (goal.event_type || 'goal').replace(/_/g, ' ').toUpperCase();
                const isSelected = selectedEventId === goal.id;

                return (
                  <button
                    key={goal.id}
                    onClick={() => setSelectedEventId(goal.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-red-700/50 border border-red-500 text-white'
                        : 'bg-gray-800/50 border border-gray-700/50 text-gray-300 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">⚽</span>
                      <div className="text-left">
                        <div className="text-sm font-semibold">{playerName}</div>
                        <div className="text-xs text-gray-400">{typeLabel}</div>
                      </div>
                    </div>
                    <span className="font-mono font-bold text-amber-400">
                      {goal.minute}'
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-gray-500 text-sm py-8">
              No goals to remove
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 font-semibold text-sm transition-colors"
          >
            Cancel
          </button>
          <PhysicalButton
            color="red"
            size="medium"
            onClick={handleConfirm}
            disabled={!selectedEventId || goals.length === 0}
          >
            Remove Goal
          </PhysicalButton>
        </div>
      </div>
    </div>
  );
};

export default RemoveGoalModal;
